import {
    DebouncedSearchInput,
    DebouncedSearchInputProps,
} from 'components/data/DebouncedSearchInput';
import { InfiniteScroll } from 'components/infinite_scroll/InfiniteScroll';
import { StatelessPopup } from 'components/popup/stateless_popup/StatelessPopup';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { InfiniteDataSource } from 'custom_hooks/data_hooks/data_source/useInfiniteDataSource';
import { useHighlightedIndex } from 'custom_hooks/useHighlightedIndex';
import { useOnClickOutside } from 'custom_hooks/useOnClickOutside';
import { MediaSize, useIsViewportAtMost } from 'custom_hooks/useWindowSize';
import * as React from 'react';
import { ReactNode, RefCallback, useEffect, useMemo, useRef } from 'react';
import useResizeAware from 'react-resize-aware';
import styled from 'styled-components';

const DefaultResultsPane = styled.div`
    display: flex;
    flex-direction: column;
    padding: 1rem;
`;

export type AutoCompleteRenderInfo<TData> = Readonly<{
    /**
     * The data item to be rendered.
     */
    item: TData;
    /**
     * If the item is currently highlighted.
     */
    isHighlighted: boolean;
    /**
     * The index of the current item in the `results` list
     * (is not affected if `defaultResult` is visible).
     */
    index: number;
    /**
     * Callback for when item is selected.
     */
    onResultSelect: () => void;
    /**
     * Ref that should be applied to the result for auto scroll.
     */
    resultRef: RefCallback<HTMLElement>;
}>;

export type AutoCompleteDataSourceConfig<TData> = Pick<
    InfiniteDataSource<TData, unknown>,
    'isLoading' | 'hasNext' | 'loadNext'
>;

export type AutoCompleteInputProps<TData> = Readonly<{
    /**
     * The current filter text.
     */
    filterText: string;
    /**
     * Callback for when the filter text changes.
     */
    onFilterTextChange: (filterText: string) => void;
    /**
     * Placeholder text for filter.
     */
    filterPlaceholder?: string;
    /**
     * Current set of results.
     */
    results: TData[] | undefined;
    /**
     * Callback to get a unique key for each result.
     */
    resultKey: (result: TData) => number | string;
    /**
     * Callback to render the results in the results popup.
     */
    renderResult: (renderInfo: AutoCompleteRenderInfo<TData>) => ReactNode;
    /**
     * Options callback to customize the container that the results will be rendered with.
     */
    renderResultsPane?: (resultsNode: ReactNode) => ReactNode;
    /**
     * When true, the results popup is visible.
     */
    isPopupOpen: boolean;
    /**
     * Callback for when the results popup visibility changes.
     */
    onPopupChange: (isOpen: boolean) => void;
    /**
     * The current highlighted index in the popup.
     */
    highlightedIndex: number;
    /**
     * Callback for when highlighted index changes.
     */
    onHighlightedIndexChange: (highlightedIndex: number) => void;
    /**
     * Callback to handle when a selection is made.
     */
    onSelect: (result: TData) => void;
    /**
     * Optional data configuration if multiple pages of results.
     */
    dataSource?: AutoCompleteDataSourceConfig<TData>;
    /**
     * Optional max height for the popup (defaults to `'30rem'`).
     */
    popupMaxHeight?: string;
    /**
     * Optional classes to apply to the input.
     */
    className?: string;
    /**
     * Optional classes to apply to the popup.
     */
    popupClassName?: string;
    /**
     * Disabled input.
     */
    isDisabled?: boolean;
    /**
     * Option title to show in the tooltip
     */
    title?: string;
}> &
    Pick<DebouncedSearchInputProps, 'maxLength'>;

/**
 * Component for rendering auto complete input.
 */
export function AutoCompleteInput<TData>({
    filterText,
    filterPlaceholder,
    onFilterTextChange,
    results,
    resultKey,
    renderResult,
    renderResultsPane,
    dataSource,
    isPopupOpen,
    onPopupChange,
    highlightedIndex,
    onHighlightedIndexChange,
    onSelect,
    className,
    popupClassName,
    popupMaxHeight = '30rem',
    isDisabled = false,
    maxLength,
    title,
}: AutoCompleteInputProps<TData>) {
    const [resizeListener, { width }] = useResizeAware();
    const isFullScreen = useIsViewportAtMost(MediaSize.xsmall);
    const inputRef = useRef<HTMLInputElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const resultRefs = useRef<(HTMLElement | null)[]>([]);

    useOnClickOutside(
        [inputRef, popupRef],
        () => {
            onPopupChange(false);
        },
        isPopupOpen,
    );

    const handleKey = useHighlightedIndex({
        items: results,
        highlightedIndex,
        onChange: onHighlightedIndexChange,
        onSelect,
    });

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (isDisabled) {
            return;
        }
        if (isPopupOpen) {
            if (event.code === 'Escape') {
                onPopupChange(false);
                event.stopPropagation();
                event.preventDefault();
            } else if (!dataSource?.isLoading) {
                handleKey(event);
            }
        } else if (event.code === 'ArrowDown') {
            onPopupChange(true);
            event.stopPropagation();
            event.preventDefault();
        }
    };

    const onClick = () => {
        if (!isPopupOpen) {
            onPopupChange(true);
        }
    };

    const onChange = (newFilterText: string) => {
        if (!isPopupOpen) {
            onPopupChange(true);
        }
        onFilterTextChange(newFilterText);
    };

    const renderedItems = useMemo(
        () =>
            results?.map<ReactNode>((item, index) => (
                <React.Fragment key={resultKey(item)}>
                    {renderResult({
                        item,
                        index,
                        onResultSelect: () => onSelect(item),
                        isHighlighted: index === highlightedIndex,
                        resultRef: (instance: HTMLElement | null) => {
                            resultRefs.current[index] = instance;
                        },
                    })}
                </React.Fragment>
            )) ?? [],
        [results, resultKey, renderResult, highlightedIndex, onSelect],
    );

    useEffect(() => {
        if (isPopupOpen) {
            window.setTimeout(() => {
                resultRefs.current[highlightedIndex]?.scrollIntoView({
                    block: 'nearest',
                });
            });
        }
    }, [highlightedIndex, isPopupOpen]);

    useEffect(() => {
        if (isDisabled && isPopupOpen) {
            onPopupChange(false);
        }
    }, [isDisabled, isPopupOpen, onPopupChange]);

    return (
        <>
            <DebouncedSearchInput
                ref={inputRef}
                value={filterText}
                onChange={onChange}
                onClick={onClick}
                placeholder={filterPlaceholder}
                onKeyDown={onKeyDown}
                className={className}
                maxLength={maxLength}
                disabled={isDisabled}
                title={title}
            />
            <div style={{ position: 'relative' }}>{resizeListener}</div>
            {isPopupOpen && ((results && results.length > 0) || isFullScreen) && (
                <StatelessPopup
                    ref={popupRef}
                    anchor={inputRef.current}
                    noOverlay
                    noPadding
                    popupClassName={popupClassName}
                    isFullscreen={isFullScreen}
                    onClose={() => onPopupChange(false)}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                            maxHeight: isFullScreen ? undefined : popupMaxHeight,
                            height: isFullScreen ? '100%' : undefined,
                            width: isFullScreen ? undefined : `${width}px`,
                        }}
                    >
                        {/* Only display this input in full screen mode */}
                        {isFullScreen && (
                            <DebouncedSearchInput
                                value={filterText}
                                onChange={onChange}
                                placeholder={filterPlaceholder}
                                onKeyDown={onKeyDown}
                                className={className}
                                maxLength={maxLength}
                                autoFocus
                            />
                        )}
                        <InfiniteScroll
                            isLoading={dataSource?.isLoading ?? true}
                            onLoadMore={dataSource?.loadNext}
                            enabled={
                                dataSource !== undefined &&
                                !dataSource.isLoading &&
                                dataSource.hasNext === true
                            }
                            style={{
                                position: 'relative',
                                flexGrow: isFullScreen ? 1 : undefined,
                                minHeight: dataSource ? '1rem' : undefined,
                                overflowY: 'auto',
                            }}
                        >
                            <SpinnerArea isLoading={dataSource?.isLoading}>
                                {renderResultsPane ? (
                                    renderResultsPane(renderedItems)
                                ) : (
                                    <DefaultResultsPane>
                                        {renderedItems}
                                    </DefaultResultsPane>
                                )}
                            </SpinnerArea>
                        </InfiniteScroll>
                    </div>
                </StatelessPopup>
            )}
        </>
    );
}
